import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { ArrowLeft, Plus, Users, Trash2, Edit2, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Circles - Visibility Control (NOT a content category)
 * 
 * Circles have ONLY TWO STATES:
 * - Private: Personal memory, not shared
 * - Public: Eligible for sharing to selected circles
 * 
 * NO predefined subcategories. NO activity labels. NO system-created subgroups.
 * Users create their own circles and name them freely.
 */

export default function Circles() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCircleName, setNewCircleName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    if (user) {
      fetchCircles();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchCircles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/circles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCircles(data.circles || []);
      }
    } catch (err) {
      console.error('Error fetching circles:', err);
    } finally {
      setLoading(false);
    }
  };

  const createCircle = async () => {
    if (!newCircleName.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/circles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newCircleName.trim() })
      });
      
      if (res.ok) {
        setNewCircleName('');
        setShowCreate(false);
        fetchCircles();
      }
    } catch (err) {
      console.error('Error creating circle:', err);
    }
  };

  const updateCircle = async (circleId) => {
    if (!editName.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/circles/${circleId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: editName.trim() })
      });
      
      setEditingId(null);
      setEditName('');
      fetchCircles();
    } catch (err) {
      console.error('Error updating circle:', err);
    }
  };

  const deleteCircle = async (circleId) => {
    if (!confirm('Delete this circle?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/circles/${circleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      fetchCircles();
    } catch (err) {
      console.error('Error deleting circle:', err);
    }
  };

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FAFAF8' }}>
        <header 
          className="sticky top-0 z-40 px-4"
          style={{ 
            backgroundColor: '#FAFAF8',
            borderBottom: '1px solid #E5E5E0'
          }}
        >
          <div className="max-w-2xl mx-auto flex items-center gap-3 h-14">
            <button onClick={() => navigate('/')} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5" style={{ color: '#1A1A1A' }} />
            </button>
            <h1 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Circles</h1>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <img 
            src="/images/tiles/tile_circles.png" 
            alt=""
            className="w-48 h-48 mx-auto mb-6 object-contain"
          />
          <h2 className="text-xl font-semibold mb-2" style={{ color: '#1A1A1A' }}>
            Your Circles
          </h2>
          <p className="mb-6" style={{ color: '#666' }}>
            Create circles to control who sees what you share.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="px-6 py-3 rounded-full font-medium"
            style={{ backgroundColor: '#4CAF50', color: 'white' }}
          >
            Sign in to create circles
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAF8' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-40 px-4"
        style={{ 
          backgroundColor: '#FAFAF8',
          borderBottom: '1px solid #E5E5E0'
        }}
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5" style={{ color: '#1A1A1A' }} />
            </button>
            <h1 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Circles</h1>
          </div>
          
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium"
            style={{ backgroundColor: '#4CAF50', color: 'white' }}
          >
            <Plus className="w-4 h-4" />
            New
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Explanation */}
        <div 
          className="p-4 rounded-xl mb-6"
          style={{ backgroundColor: '#F5F5F0' }}
        >
          <p className="text-sm" style={{ color: '#666' }}>
            Circles let you control who sees your photos and moments. 
            When you share something as <strong>Public</strong>, you choose which circles can see it.
            <strong> Private</strong> items are only for you.
          </p>
        </div>

        {/* Create Circle Modal */}
        {showCreate && (
          <div 
            className="mb-6 p-4 rounded-xl"
            style={{ backgroundColor: 'white', border: '1px solid #E5E5E0' }}
          >
            <h3 className="font-medium mb-3" style={{ color: '#1A1A1A' }}>Create Circle</h3>
            <input
              type="text"
              value={newCircleName}
              onChange={(e) => setNewCircleName(e.target.value)}
              placeholder="Circle name (e.g., Close Friends, Family)"
              className="w-full px-3 py-2 rounded-lg border mb-3"
              style={{ borderColor: '#E5E5E0' }}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={createCircle}
                className="px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: '#4CAF50', color: 'white' }}
              >
                Create
              </button>
              <button
                onClick={() => { setShowCreate(false); setNewCircleName(''); }}
                className="px-4 py-2 rounded-lg"
                style={{ color: '#666' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Circles List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="animate-pulse p-4 rounded-xl" style={{ backgroundColor: '#F5F5F0' }}>
                <div className="h-5 w-32 rounded" style={{ backgroundColor: '#E5E5E0' }} />
              </div>
            ))}
          </div>
        ) : circles.length > 0 ? (
          <div className="space-y-3">
            {circles.map(circle => (
              <div
                key={circle.id}
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'white', border: '1px solid #E5E5E0' }}
              >
                {editingId === circle.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border"
                      style={{ borderColor: '#E5E5E0' }}
                      autoFocus
                    />
                    <button onClick={() => updateCircle(circle.id)} className="p-2">
                      <Check className="w-5 h-5" style={{ color: '#4CAF50' }} />
                    </button>
                    <button onClick={() => { setEditingId(null); setEditName(''); }} className="p-2">
                      <X className="w-5 h-5" style={{ color: '#999' }} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: '#F5F5F0' }}
                      >
                        <Users className="w-5 h-5" style={{ color: '#666' }} />
                      </div>
                      <div>
                        <h3 className="font-medium" style={{ color: '#1A1A1A' }}>{circle.name}</h3>
                        <p className="text-sm" style={{ color: '#999' }}>
                          {circle.member_count} {circle.member_count === 1 ? 'person' : 'people'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => { setEditingId(circle.id); setEditName(circle.name); }}
                        className="p-2"
                      >
                        <Edit2 className="w-4 h-4" style={{ color: '#999' }} />
                      </button>
                      <button 
                        onClick={() => deleteCircle(circle.id)}
                        className="p-2"
                      >
                        <Trash2 className="w-4 h-4" style={{ color: '#999' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <img 
              src="/images/tiles/tile_circles.png" 
              alt=""
              className="w-32 h-32 mx-auto mb-4 object-contain opacity-50"
            />
            <p style={{ color: '#666' }}>No circles yet</p>
            <p className="text-sm mt-1" style={{ color: '#999' }}>
              Create circles to organize who sees your content
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
